import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Award, Users, Target } from 'lucide-react';
import sofia from '../assets/sofia.png'; 
import joao from '../assets/joao.jpg';

const Team = () => {
  const teamMembers = [
    { 
      name: 'João Rocha Gomes', 
      role: 'Founder & CEO', 
      description: 'Medical Doctor, Health Economist, PhD Candidate Data Science for Health',
      image: joao,
      linkedin: 'https://www.linkedin.com/in/joaorochagomes/' 
    },
    { 
      name: 'Sofia Teixeira', 
      role: 'Founder & CTO', 
      description: 'Msc Electrical Engineering, PhD Candidate in Informatics Engineering',
      image: sofia,
      linkedin: 'https://www.linkedin.com/in/ana-sofia-teixeira/' 
    }
  ];

  const advisoryFields = [
    'Health Data Science',
    'Oncology',
    'Medical Research', 
    'Healthcare Management',
    'Clinical Informatics',
    'Regulatory Affairs'
  ];

  const stats = [
    { icon: <Users size={24} />, number: '3', label: 'Team Members' },
    { icon: <Award size={24} />, number: '2', label: 'Co-Founders' },
    { icon: <Target size={24} />, number: '100%', label: 'Dedicated to Innovation' }
  ];

  return (
    <Section id="team">
      <BackgroundElements>
        <FloatingOrb style={{ top: '15%', left: '5%' }} />
        <FloatingOrb style={{ top: '75%', right: '10%' }} />
        <GridPattern />
      </BackgroundElements>
      
      <Content>
        <HeaderSection>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <TeamLabel>Our Team</TeamLabel>
            <SectionTitle>
              Meet Our <GradientText>Team</GradientText>
            </SectionTitle>
          </motion.div>
        </HeaderSection>

        {/* Team Members */}
        <TeamSection>
          <Grid>
            {teamMembers.map((member, index) => (
              <Card
                key={index}
                as={motion.div}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 + index * 0.2 }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <CardHeader>
                  <Avatar src={member.image} alt={member.name} />
                </CardHeader>
                
                <CardContent>
                  <MemberName>{member.name}</MemberName>
                  <Role>{member.role}</Role>
                  <Description>{member.description}</Description>
                  
                  {member.linkedin && (
                    <SocialLinks>
                      <SocialIcon 
                        href={member.linkedin} 
                        target="_blank" 
                        aria-label="LinkedIn"
                        as={motion.a}
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Linkedin size={20} />
                      </SocialIcon>
                    </SocialLinks>
                  )}
                </CardContent>
              </Card>
            ))}
          </Grid>

          {/* Advisory Panel */}
          <AdvisoryPanel
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            whileHover={{ y: -5, transition: { duration: 0.3 } }}
          >
            <AdvisoryHeader>
              <AdvisoryTitle>Advisory Panel</AdvisoryTitle>
              <AdvisorySubtitle>Expert Panel</AdvisorySubtitle>
            </AdvisoryHeader>
            
            <AdvisoryDescription>
              We have specialists across several key fields supporting our mission
            </AdvisoryDescription>
            
            <ExpertiseGrid>
              {advisoryFields.map((field, index) => (
                <ExpertiseTag key={index}>
                  {field}
                </ExpertiseTag>
              ))}
            </ExpertiseGrid>
          </AdvisoryPanel>
        </TeamSection>
      </Content>
    </Section>
  );
};
const Section = styled.section`
  padding: 80px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  position: relative;
  overflow: hidden;
`;

const BackgroundElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
`;

const FloatingOrb = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(147, 51, 234, 0.1) 0%, transparent 70%);
  filter: blur(40px);
  animation: float 6s ease-in-out infinite;
  
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-20px); }
  }
`;

const GridPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 80px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.4), transparent);
  }
`;

const TeamLabel = styled.div`
  display: inline-block;
  color: #9333ea;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const SectionTitle = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #1a1a1a;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #2094F1 0%, #AF54AE 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  font-size: 1.3rem;
  line-height: 1.7;
  color: #64748b;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const StatsSection = styled.div`
  margin-bottom: 80px;
  position: relative;
  padding-top: 50px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.3), transparent);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
  max-width: 800px;
  margin: 0 auto;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  padding: 30px 20px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.3), transparent);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.1);
    background: rgba(255, 255, 255, 0.95);
    
    &::before {
      transform: scaleX(1);
    }
  }
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin: 0 auto 15px;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 0.95rem;
  color: #64748b;
  font-weight: 500;
`;

const TeamSection = styled.div`
  position: relative;
  padding-top: 40px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 180px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(147, 51, 234, 0.4), rgba(59, 130, 246, 0.4), transparent);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-top: 60px;
  margin-bottom: 40px;
`;

const AdvisoryPanel = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px 30px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: none;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.5), rgba(147, 51, 234, 0.5), transparent);
    transition: left 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.15);
    
    &::before {
      left: 100%;
    }
  }
`;

const AdvisoryHeader = styled.div`
  margin-bottom: 20px;
`;

const AdvisoryTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const AdvisorySubtitle = styled.p`
  font-size: 1.1rem;
  color: #9333ea;
  font-weight: 600;
  margin-bottom: 0;
`;

const AdvisoryDescription = styled.p`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ExpertiseGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  max-width: 700px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px 30px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.5), rgba(147, 51, 234, 0.5), transparent);
    transition: left 0.6s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.15);
    
    &::before {
      left: 100%;
    }
  }
`;

const CardHeader = styled.div`
  position: relative;
  margin-bottom: 25px;
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  position: relative;
  z-index: 2;
  border: 4px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
`;

const MemberName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1a1a1a;
`;

const Role = styled.p`
  font-size: 1.1rem;
  color: #9333ea;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Description = styled.p`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const ExpertiseList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-bottom: 25px;
`;

const ExpertiseTag = styled.span`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
  color: #6366f1;
  padding: 8px 16px;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid rgba(99, 102, 241, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
    border-color: rgba(99, 102, 241, 0.3);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const SocialIcon = styled.a`
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(147, 51, 234, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(147, 51, 234, 0.4);
  }
`;

export default Team;
